import React, { useCallback, useEffect, useRef, useState } from "react";
import { Network } from "vis-network/standalone";
import OrgIcon from "../../../icons/OverviewOrgIcon.svg";
import ThemeIcon from "../../../icons/OverviewThemeIcon.svg";
import AuditsIcon from "../../../icons/OverviewAuditsIcon.svg";
import ComplaintIcon from "../../../icons/OverviewCompliantIcon.svg";
import NonComplaintIcon from "../../../icons/OverviewNonComplaintIcon.svg";
import Calendar from "../../../icons/OverviewOrgCalendar.svg";
import FilterLegend from "../ChartLegend";
import styles from "./styles.module.scss";

interface NetworkNode {
    nodeType: "THEME" | "ORGANIZATION" | "YEAR" | "AUDIT" | "COMPLIANT" | "NON_COMPLIANT";
    name: string;
    children?: NetworkNode[];
}

interface NetworkData {
    networkGraphRootType?: string;
    rootType?: string; // Keep for backward compatibility
    network: NetworkNode[];
}

interface Props {
    data: NetworkData;
    onNodeClick?: (nodeData: NetworkNode) => void;
    nodeToExpand?: NetworkNode | null;
    resetExpandedNodes?: boolean;
}

const VisNetworkDiagram: React.FC<Props> = ({ data, onNodeClick, nodeToExpand, resetExpandedNodes }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const networkRef = useRef<Network | null>(null);
    const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
    const [layoutMode, setLayoutMode] = useState<'hierarchical' | 'physics'>('hierarchical');
    const animationIntervalsRef = useRef<NodeJS.Timeout[]>([]); // Store animation intervals in ref
    const nodeDataMapRef = useRef<Map<string, NetworkNode>>(new Map()); // Store node data mapping
    const parentChildMapRef = useRef<Map<string, { parentId: string | null; siblingIds: string[] }>>(new Map()); // Track parent-child relationships

    // Get icon SVG string for each node type
    const getIconSvg = (nodeType: string) => {
        let iconPath = "";

        switch (nodeType) {
            case "THEME":
                iconPath = ThemeIcon;
                break;
            case "ORGANIZATION":
                iconPath = OrgIcon;
                break;
            case "YEAR":
                iconPath = Calendar;
                break;
            case "AUDIT":
                iconPath = AuditsIcon;
                break;
            case "COMPLIANT":
                iconPath = ComplaintIcon;
                break;
            case "NON_COMPLIANT":
                iconPath = NonComplaintIcon;
                break;
            default:
                return "";
        }

        // Return the direct path to the SVG file
        // vis.js will handle loading and displaying it
        return iconPath;
    };

    // Truncate text with ellipsis
    const truncateText = (text: string, maxLength = 12) => {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength - 3) + "...";
    };

    // Reset expanded nodes when filters change
    useEffect(() => {
        if (resetExpandedNodes) {
            setExpandedNodes(new Set());
        }
    }, [resetExpandedNodes]);

    useEffect(() => {
        if (!containerRef.current) return;

        // Group audits by year - handles both ORGANIZATION and THEME root types
        const groupAuditsByYear = (network: NetworkNode[]): NetworkNode[] => {
            if (!network || network.length === 0) return network;

            const rootType = network[0].nodeType;

            if (rootType === 'ORGANIZATION') {
                // ORGANIZATION → THEME → YEAR → AUDIT structure
                // Group years within each theme separately to ensure unique node instances
                return network.map(org => ({
                    ...org,
                    children: org.children?.map(theme => {
                        if (theme.nodeType !== 'THEME' || !theme.children) return theme;

                        const yearMap = new Map<string, NetworkNode>();

                        // Collect all audits grouped by year for this specific theme
                        theme.children.forEach(yearNode => {
                            if (yearNode.nodeType === 'YEAR') {
                                const yearStr = new Date(yearNode.name).getFullYear().toString();

                                if (!yearMap.has(yearStr)) {
                                    yearMap.set(yearStr, {
                                        nodeType: 'YEAR',
                                        name: yearNode.name,
                                        children: []
                                    });
                                }

                                if (yearNode.children) {
                                    yearMap.get(yearStr)!.children!.push(...yearNode.children);
                                }
                            }
                        });

                        return {
                            ...theme,
                            children: Array.from(yearMap.values())
                        };
                    })
                }));

            } else if (rootType === 'THEME') {
                // THEME → ORGANIZATION → YEAR → AUDIT structure
                // Group years within each organization
                return network.map(theme => {
                    if (!theme.children) return theme;

                    // For each organization, group its years
                    const processedOrgs = theme.children.map(org => {
                        if (org.nodeType !== 'ORGANIZATION' || !org.children) return org;

                        const yearMap = new Map<string, NetworkNode>();

                        org.children.forEach(yearNode => {
                            if (yearNode.nodeType === 'YEAR') {
                                const yearStr = new Date(yearNode.name).getFullYear().toString();

                                if (!yearMap.has(yearStr)) {
                                    yearMap.set(yearStr, {
                                        nodeType: 'YEAR',
                                        name: yearNode.name,
                                        children: []
                                    });
                                }

                                if (yearNode.children) {
                                    yearMap.get(yearStr)!.children!.push(...yearNode.children);
                                }
                            }
                        });

                        return {
                            ...org,
                            children: Array.from(yearMap.values())
                        };
                    });

                    return {
                        ...theme,
                        children: processedOrgs
                    };
                });
            }

            return network;
        };

        // Apply year grouping
        const processedNetwork = groupAuditsByYear(data.network);

        // Transform hierarchical data to vis.js nodes and edges
        const transformData = () => {
            const nodes: any[] = [];
            const edges: any[] = [];

            const getNodeColor = (nodeType: string) => {
                switch (nodeType) {
                    case "THEME":
                        return { background: "#a78bfa" };
                    case "ORGANIZATION":
                        return { background: "#1e3a8a" };
                    case "YEAR":
                        return { background: "#6b7280" };
                    case "AUDIT":
                        return { background: "#06b6d4" };
                    case "COMPLIANT":
                        return { background: "#10b981" };
                    case "NON_COMPLIANT":
                        return { background: "#ef4444" };
                    default:
                        return { background: "#94a3b8" };
                }
            };

            // Create a unique ID based on the full path from root to node
            const createNodeId = (parentId: string | null, nodeType: string, nodeName: string, index: number): string => {
                if (parentId === null) {
                    return `${nodeType}-${nodeName}-${index}`;
                }
                return `${parentId}|${nodeType}-${nodeName}-${index}`;
            };

            const processNode = (node: NetworkNode, parentId: string | null = null, level = 0, childIndex = 0, parentNode: NetworkNode | null = null) => {
                const id = createNodeId(parentId, node.nodeType, node.name, childIndex);
                const color = getNodeColor(node.nodeType);
                const isExpanded = expandedNodes.has(id);
                const iconSvg = getIconSvg(node.nodeType);

                // Store node data for click handling
                nodeDataMapRef.current.set(id, node);

                // Calculate sibling IDs - siblings are other children of the same parent
                const siblingIds: string[] = [];
                if (parentNode && parentNode.children) {
                    parentNode.children.forEach((sibling, idx) => {
                        if (idx !== childIndex) {
                            const siblingId = createNodeId(parentId, sibling.nodeType, sibling.name, idx);
                            siblingIds.push(siblingId);
                        }
                    });
                }

                // Store parent-child relationship
                parentChildMapRef.current.set(id, { parentId, siblingIds });

                // Format year nodes to show only the year
                let displayName = node.name;
                if (node.nodeType === "YEAR") {
                    try {
                        const date = new Date(node.name);
                        displayName = date.getFullYear().toString();
                    } catch {
                        displayName = node.name; // Keep original if parsing fails
                    }
                }

                // Truncate label and use full name in title for tooltip
                const displayLabel = truncateText(displayName, 15);
                const fullLabel = displayName;

                // Create vis.js node with image icon
                nodes.push({
                    id,
                    label: displayLabel,
                    // title: `${node.nodeType}: ${fullLabel}`,
                    image: {
                        unselected: iconSvg,
                        selected: iconSvg,
                    },
                    shape: "circularImage",
                    size: 28,
                    color: {
                        background: color.background,
                        highlight: {
                            background: color.background,
                        },
                    },
                    borderWidth: 0,
                    borderWidthSelected: 0,
                    font: {
                        color: "#374151",
                        size: 13,
                        face: "Arial, sans-serif",
                        bold: false,
                        vadjust: 2,
                    },
                    shadow: {
                        enabled: true,
                        color: "rgba(0, 0, 0, 0.15)",
                        size: 10,
                        x: 0,
                        y: 4,
                    },
                    shapeProperties: {
                        useImageSize: false,
                        useBorderWithImage: true,
                        interpolation: true,
                    },
                    imagePadding: 15,
                    nodeType: node.nodeType,
                    hasChildren: node.children && node.children.length > 0,
                    level,
                    fullName: fullLabel,
                    childrenCount: node.children?.length || 0,
                });

                // Create edge from parent with unique ID for animation
                if (parentId !== null) {
                    edges.push({
                        id: `edge-${parentId}-${id}`,
                        from: parentId,
                        to: id,
                        color: {
                            color: "#c7d2fe",
                            opacity: 0.6,
                        },
                        dashes: [3, 3],
                        width: 2,
                        smooth: {
                            enabled: true,
                            type: "cubicBezier",
                            roundness: 0.5,
                        },
                    });
                }

                // Process children - show all if expanded or at root level
                if (node.children && (level === 0 || isExpanded)) {
                    // Sort children alphabetically by name before processing
                    const sortedChildren = [...node.children].sort((a, b) => {
                        const nameA = a.name.toLowerCase();
                        const nameB = b.name.toLowerCase();
                        return nameA.localeCompare(nameB);
                    });

                    sortedChildren.forEach((child, index) => {
                        processNode(child, id, level + 1, index, node);
                    });
                }

                return id;
            };

            // Process all root nodes
            processedNetwork.forEach((node, index) => processNode(node, null, 0, index, null));

            return { nodes, edges };
        };

        const { nodes, edges } = transformData();

        const visData = {
            nodes,
            edges,
        };

        const options = {
            layout: layoutMode === 'hierarchical' ? {
                hierarchical: {
                    enabled: true,
                    direction: "LR",
                    sortMethod: "directed",
                    levelSeparation: 200,
                    nodeSpacing: 150,
                    treeSpacing: 200,
                },
            } : {
                hierarchical: {
                    enabled: false,
                },
            },
            physics: {
                enabled: layoutMode === 'physics',
                barnesHut: {
                    gravitationalConstant: -2000,
                    centralGravity: 0.3,
                    springLength: 200,
                    springConstant: 0.04,
                    damping: 0.09,
                    avoidOverlap: 0.5,
                },
                solver: "barnesHut",
                stabilization: {
                    enabled: layoutMode === 'physics',
                    iterations: 2000,
                    updateInterval: 25,
                    fit: true,
                },
            },
            interaction: {
                hover: true,
                hoverConnectedEdges: false,
                zoomView: true,
                dragView: true,
                zoomSpeed: 1,
                navigationButtons: true,
                keyboard: {
                    enabled: false,
                },
            },
            manipulation: {
                enabled: false,
            },
            nodes: {
                borderWidthSelected: 0,
                size: 30,
                fixed: {
                    x: false,
                    y: false,
                },
                font: {
                    size: 13,
                    face: "Arial, sans-serif",
                    multi: false,
                    bold: {
                        size: 13,
                    },
                },
                scaling: {
                    min: 30,
                    max: 30,
                },
                shapeProperties: {
                    interpolation: true,
                },
            },
            edges: {
                hoverWidth: 0,
                hoverWidthSelected: 0,
                selectionWidth: 0,
                smooth: {
                    enabled: true,
                    type: "dynamic",
                    roundness: 0.5,
                },
                length: 200,
            },
        };

        // Create network
        const network = new Network(containerRef.current, visData, options);
        networkRef.current = network;

        // Set zoom limits
        const MIN_ZOOM = 0.3;
        const MAX_ZOOM = 2.0;

        network.on("zoom", () => {
            const scale = network.getScale();
            if (scale > MAX_ZOOM) {
                network.moveTo({
                    scale: MAX_ZOOM,
                    position: network.getViewPosition(),
                    animation: false
                });
            } else if (scale < MIN_ZOOM) {
                network.moveTo({
                    scale: MIN_ZOOM,
                    position: network.getViewPosition(),
                    animation: false
                });
            }
        });

        network.on("hoverNode", () => {
            if (containerRef.current) {
                containerRef.current.style.cursor = "pointer";
            }
        });

        network.on("blurNode", () => {
            if (containerRef.current) {
                containerRef.current.style.cursor = "default";
            }
        });

        // Handle node clicks for expand/collapse and modal
        network.on("click", (params) => {
            if (params.nodes.length > 0) {
                const clickedNodeId = params.nodes[0];
                const clickedNode = nodes.find((n) => n.id === clickedNodeId);

                // Get the original node data for the modal
                const nodeData = nodeDataMapRef.current.get(clickedNodeId);
                if (nodeData && onNodeClick) {
                    onNodeClick(nodeData);
                }

                if (clickedNode && clickedNode.hasChildren && clickedNode.nodeType !== "COMPLIANT" && clickedNode.nodeType !== "NON_COMPLIANT") {
                    setExpandedNodes((prev) => {
                        const newSet = new Set(prev);

                        // Helper function to remove all descendants
                        const removeDescendants = (nodeId: string) => {
                            const connectedNodes = network.getConnectedNodes(nodeId, 'to');
                            connectedNodes.forEach((descendantId) => {
                                const descendantIdStr = String(descendantId);
                                if (newSet.has(descendantIdStr)) {
                                    newSet.delete(descendantIdStr);
                                    // Recursively remove their descendants too
                                    removeDescendants(descendantIdStr);
                                }
                            });
                        };

                        if (newSet.has(clickedNodeId)) {
                            // Already expanded - collapse it and all its descendants
                            newSet.delete(clickedNodeId);
                            removeDescendants(clickedNodeId);
                        } else {
                            // Not expanded - expand it and collapse siblings

                            // First, collapse all sibling nodes and their descendants
                            const relationshipInfo = parentChildMapRef.current.get(clickedNodeId);
                            if (relationshipInfo && relationshipInfo.siblingIds.length > 0) {
                                relationshipInfo.siblingIds.forEach(siblingId => {
                                    if (newSet.has(siblingId)) {
                                        newSet.delete(siblingId);
                                        removeDescendants(siblingId);
                                    }
                                });
                            }

                            // Then expand the clicked node
                            newSet.add(clickedNodeId);
                        }

                        return newSet;
                    });
                }
            }
        });

        return () => {
            // Clean up animations before destroying network
            animationIntervalsRef.current.forEach((interval) => clearInterval(interval));
            animationIntervalsRef.current = [];

            // Destroy network
            if (networkRef.current) {
                networkRef.current.destroy();
                networkRef.current = null;
            }
        };
    }, [data, expandedNodes, layoutMode]);

    // Handle node expansion from external trigger (e.g., modal navigation)
    useEffect(() => {
        if (!nodeToExpand) return;

        // Check if this is a collapse request
        const shouldCollapse = (nodeToExpand as any)._shouldCollapse;

        // Find the node ID that matches this node data
        let matchingNodeId: string | null = null;
        nodeDataMapRef.current.forEach((nodeData, nodeId) => {
            if (nodeData.nodeType === nodeToExpand.nodeType && nodeData.name === nodeToExpand.name) {
                matchingNodeId = nodeId;
            }
        });

        if (matchingNodeId) {
            if (shouldCollapse) {
                // Collapse this node and all its descendants
                setExpandedNodes((prev) => {
                    const newSet = new Set(prev);

                    // Remove descendants recursively
                    const removeDescendants = (nodeId: string) => {
                        if (networkRef.current) {
                            const connectedNodes = networkRef.current.getConnectedNodes(nodeId, 'to');
                            connectedNodes.forEach((descendantId) => {
                                const descendantIdStr = String(descendantId);
                                if (newSet.has(descendantIdStr)) {
                                    newSet.delete(descendantIdStr);
                                    removeDescendants(descendantIdStr);
                                }
                            });
                        }
                    };

                    // Remove the node itself and all descendants
                    if (newSet.has(matchingNodeId!)) {
                        newSet.delete(matchingNodeId!);
                        removeDescendants(matchingNodeId!);
                    }

                    return newSet;
                });
            } else {
                // Expand this node
                setExpandedNodes((prev) => {
                    const newSet = new Set(prev);

                    // Collapse all siblings
                    const relationshipInfo = parentChildMapRef.current.get(matchingNodeId!);
                    if (relationshipInfo && relationshipInfo.siblingIds.length > 0) {
                        relationshipInfo.siblingIds.forEach(siblingId => {
                            if (newSet.has(siblingId)) {
                                newSet.delete(siblingId);
                                // Remove descendants recursively
                                const removeDescendants = (nodeId: string) => {
                                    if (networkRef.current) {
                                        const connectedNodes = networkRef.current.getConnectedNodes(nodeId, 'to');
                                        connectedNodes.forEach((descendantId) => {
                                            const descendantIdStr = String(descendantId);
                                            if (newSet.has(descendantIdStr)) {
                                                newSet.delete(descendantIdStr);
                                                removeDescendants(descendantIdStr);
                                            }
                                        });
                                    }
                                };
                                removeDescendants(siblingId);
                            }
                        });
                    }

                    // Add the matching node to expanded set
                    newSet.add(matchingNodeId!);
                    return newSet;
                });
            }
        }
    }, [nodeToExpand]);

    // Toggle layout mode
    const toggleLayoutMode = () => {
        setLayoutMode((prev) => prev === 'hierarchical' ? 'physics' : 'hierarchical');
    };

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex' }}>
            {/* Chart Legend */}
            <FilterLegend />

            {/* Network Graph */}
            <div style={{ flex: 1, position: 'relative' }}>
                <div ref={containerRef} className={styles.networkContainer} />
            </div>
        </div>
    );
};

export default VisNetworkDiagram;
